import styles from "./EventArrivalConfirmation.module.scss";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { SingleSectionPageLayout } from "../../../common/components/common/SingleSectionPageLayout";
import { IEventArrivalConfirmationFormModel } from "./models/eventArrivalConfirmationFormModel";
import { nameof } from "ts-simple-nameof";
import { DefaultButton } from "../../../common/components/button/defaultButton/DefaultButton";
import { useNavigate, useParams } from "react-router-dom";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { useAppContext } from "../../../AppContext";
import { IEventArrivalConfirmationViewModel } from "./models/eventArrivalConfirmationViewModel";
import {
  getPaymentOptions,
  humanizePaymentType,
  sortPaymentOptionsByTypeOrder,
} from "../../../common/utilities/paymentUtility";
import { useMemo, useRef } from "react";
import { useEventArrivalConfirmationForm } from "./hooks/useEventArrivalConfirmationForm";
import { PaymentTypes } from "../../../common/enums";
import { RenderWhen } from "../../../common/infrastructure/components/RenderWhen";
import { useApiMutation } from "../../../common/infrastructure/hooks/useApiMutation";
import {
  constructCashdeskHref,
  constructPlannerEventHref,
} from "../../../common/infrastructure/routes/routePaths";
import { DefaultSection } from "../../../common/components/common/DefaultSection";
import { SectionInfoRow } from "../../../common/components/common/SectionInfoRow";
import { dateUtility } from "../../../common/infrastructure/utilities/dateUtility";
import { showCustomConfirmDialog } from "../../../common/components/dialogs/AppConfirmDialog";
import { DefaultToggle } from "../../../common/components/forms/inputs/toggle/DefaultToggle";
import { PaymentMethodFormFields } from "../common/PaymentMethodFormFields";
import { useDepositTenderOption } from "../common/hooks/useDepositTenderOption";

export const EventArrivalConfirmation = () => {
  const { userEventId } = useParams();
  const { selectedStudioId } = useAppContext();

  const { mutate } = useApiMutation();
  const navigate = useNavigate();

  const { data, isLoading } = useApiQuery<IEventArrivalConfirmationViewModel>({
    queryKey: [
      endpointsMetadata.planner.getEventArrivalConfirmationInfo,
      userEventId,
    ],
    endpoint: `${endpointsMetadata.planner.getEventArrivalConfirmationInfo}?userEventId=${userEventId}`,
    enabled: selectedStudioId > 0,
    disableErrorToast: false,
    retry: false,
  });

  const openCashdeskAfterRef = useRef(false);

  const submitArrival = (formData: IEventArrivalConfirmationFormModel) => {
    mutate(
      {
        endpoint: `${endpointsMetadata.planner.markUserAsArrived}`,
        globalIsLoading: true,
        method: "put",
        queryParams: {
          ...formData,
        },
        invalidateKeys: [[endpointsMetadata.authentication.getUserProfile]],
      },
      {
        onSuccess: () => {
          if (openCashdeskAfterRef.current) {
            openCashdeskAfterRef.current = false;
            navigate(constructCashdeskHref({ userId: data?.user?.id }));
          } else {
            navigate(constructPlannerEventHref(Number(data?.eventId)));
          }
        },
      },
    );
  };

  const onSave: SubmitHandler<IEventArrivalConfirmationFormModel> = async (
    formData,
  ) => {
    if (data?.isUnreliable) {
      showCustomConfirmDialog(
        "В момента потребителят е маркиран като нередовен. Искате ли да го маркирате като редовен сега?",
        () => submitArrival({ ...formData, toggleUserReliability: true }),
        () => submitArrival({ ...formData, toggleUserReliability: false }),
        "warning",
        "Да, маркирай като редовен",
        "Не, остави като нередовен",
      );
    } else {
      submitArrival(formData);
    }
  };

  const allPaymentOptions = getPaymentOptions();

  const paymentOptions = useMemo(() => {
    const filtered = allPaymentOptions.filter((x) =>
      data?.paymentTypes?.some((y) => y === x.id),
    );
    return sortPaymentOptionsByTypeOrder(filtered, data?.paymentTypes ?? []);
  }, [data, isLoading]);

  const form = useEventArrivalConfirmationForm(
    isLoading,
    paymentOptions.map((x) => x.id) as PaymentTypes[],
    data,
  );

  const selectedPaymentMethodId = form.watch("paymentTypeId");
  const subscriptionId = form.watch("subscriptionId");
  const watchedPrice = form.watch("price");
  const watchedCurrencyId = form.watch("currencyId");

  // Tender-model deposit: "Депозит" joins the "Начин на плащане" dropdown only
  // when the balance covers the WHOLE price and the price is in EUR; picked,
  // it is submitted as a regular paymentTypeId (900) — the BE re-validates.
  // A reservation already stamped PaymentType = Deposit keeps its tender: the
  // Spend row already left the ledger, so the balance no longer covers the
  // price and re-validating it would silently switch the method.
  const isDepositAlreadyTendered =
    Number(data?.selectedPaymentType ?? 0) === PaymentTypes.Deposit;

  const depositTender = useDepositTenderOption({
    depositBalance: data?.depositBalance,
    price: watchedPrice,
    selectedCurrencyId: watchedCurrencyId,
    baseOptions: paymentOptions,
    selectedPaymentTypeId: selectedPaymentMethodId,
    isDepositAlreadyTendered,
    onDepositUnavailable: (fallbackPaymentTypeId) =>
      form.setValue("paymentTypeId", fallbackPaymentTypeId),
    onDepositRestored: (depositPaymentTypeId) =>
      form.setValue("paymentTypeId", depositPaymentTypeId),
  });

  const isSelectedPaymentPlan = selectedPaymentMethodId === PaymentTypes.Plan;

  const planInfo = useMemo(
    () => data?.subscriptions.find((s) => s.id == subscriptionId),
    [selectedPaymentMethodId, subscriptionId],
  );

  const paymentTypeLabel = humanizePaymentType(
    data?.selectedPaymentType,
    undefined,
  );

  // Deposit (900) is deliberately excluded from StudioPaymentType, so the
  // studio-configured list can never contain it — it is still a perfectly
  // valid (and already charged) tender, exactly like the two BE checks that
  // carry an explicit Deposit exemption.
  const isPaymentMethodValid =
    isDepositAlreadyTendered ||
    data?.paymentTypes.includes(Number(data.selectedPaymentType ?? 0));

  const children = (
    <>
      <DefaultSection className={styles.root_userData}>
        <SectionInfoRow
          label={"Потребител"}
          data={`${data?.user.name} (#${data?.user.id})`}
          isLoading={isLoading}
        />
        <SectionInfoRow
          label={data?.isService ? "Услуга" : "Клас"}
          data={`${data?.eventName}`}
          isLoading={isLoading}
        />

        <SectionInfoRow
          label={"Дата"}
          data={`${dateUtility.formatDateTime(data?.eventDate)}`}
          isLoading={isLoading}
        />

        <RenderWhen condition={!!data?.selectedPaymentType}>
          <SectionInfoRow
            label={"Избран начин на плащане"}
            data={
              <>
                {paymentTypeLabel}{" "}
                {!isPaymentMethodValid && (
                  <span className={styles.root_invalid_payment}>
                    (Не е наличен)
                  </span>
                )}
              </>
            }
            isLoading={isLoading}
          />
        </RenderWhen>

        <RenderWhen condition={!!planInfo?.id && !!isSelectedPaymentPlan}>
          <SectionInfoRow
            label={"Aбонамент"}
            data={`${planInfo?.name} (#${planInfo?.id})`}
            isLoading={isLoading}
          />

          <SectionInfoRow
            label={"Оставащи посещения"}
            data={
              !!planInfo?.remainingVisits
                ? `${planInfo?.remainingVisits}бр.`
                : "неограничен"
            }
            isLoading={isLoading}
          />

          <RenderWhen condition={!!planInfo?.type}>
            <SectionInfoRow
              label={"Тип план"}
              data={planInfo?.type}
              isLoading={isLoading}
            />
          </RenderWhen>

          <RenderWhen condition={!!planInfo?.activeUntil}>
            <SectionInfoRow
              label={"Последна дата на валидност"}
              data={dateUtility.formatDate(planInfo?.activeUntil)}
              isLoading={isLoading}
            />
          </RenderWhen>
        </RenderWhen>

        <RenderWhen condition={depositTender.hasDepositBalance}>
          <SectionInfoRow
            label={"Депозит"}
            data={
              <span className={styles.root_deposit_balance}>
                {depositTender.depositBalanceLabel}
              </span>
            }
            isLoading={isLoading}
          />
        </RenderWhen>

        <RenderWhen condition={!!data?.isUnreliable}>
          <p className={styles.root_unreliable}>НЕРЕДОВЕН КЛИЕНТ</p>
        </RenderWhen>
      </DefaultSection>

      <PaymentMethodFormFields
        fieldNames={{
          paymentTypeId: nameof<IEventArrivalConfirmationFormModel>(
            (x) => x.paymentTypeId,
          ),
          subscriptionId: nameof<IEventArrivalConfirmationFormModel>(
            (x) => x.subscriptionId,
          ),
          price: nameof<IEventArrivalConfirmationFormModel>((x) => x.price),
          currencyId: nameof<IEventArrivalConfirmationFormModel>(
            (x) => x.currencyId,
          ),
        }}
        paymentOptions={depositTender.paymentOptions}
        subscriptions={data?.subscriptions ?? []}
        isService={data?.isService}
        serviceMinPrice={data?.serviceMinPrice}
        serviceMaxPrice={data?.serviceMaxPrice}
        disableSingleSubscription
        onPaymentTypeUserSelect={depositTender.notifyPaymentTypeSelectedByUser}
      />

      <DefaultToggle
        name={nameof<IEventArrivalConfirmationFormModel>((x) => x.onlyPaid)}
        label="Платена без присъствие"
      />
      <div className={styles.root_buttons}>
        <DefaultButton label={"Потвърди"} type="submit" />
        <DefaultButton
          label={"Потвърди и отвори каса"}
          design="outline"
          type="button"
          onClick={() => {
            openCashdeskAfterRef.current = true;
            form.handleSubmit(onSave)();
          }}
        />
      </div>
    </>
  );

  const noResults = <p className={styles.no_results}>Невалидна заявка.</p>;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSave)}>
        <SingleSectionPageLayout
          title={"Потвърждение за присъствие"}
          innerClassName={styles.layout}
          children={!isLoading && !data ? noResults : children}
          maxHeight={"80%"}
        />
      </form>
    </FormProvider>
  );
};
